/**
 * SCRUM-241 — Structura Picking: recepciones.
 *
 * Las recepciones (sm_stock_receipts) se crean ya confirmadas, no tienen ciclo
 * pendiente→recibida, así que no hay "recepciones pendientes" que listar. Lo que
 * está pendiente es una orden de compra enviada: eso es lo que mostramos, más la
 * opción de recibir sin OC, que el modelo admite (purchase_order_id es opcional).
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Loader2, PackagePlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PickingShell } from "@/components/picking/PickingShell";
import { pickingJson } from "@/lib/picking/session";

export const Route = createFileRoute("/picking/recepciones/")({
  component: RecepcionesPage,
  head: () => ({ meta: [{ title: "Recepciones — Structura Picking" }] }),
});

interface PurchaseOrderSummary {
  id: string;
  supplierName?: string;
  status: string;
  expectedAt: string | null;
  createdAt: string;
  items: Array<{ quantity: number; receivedQuantity: number }>;
}

/** Una OC sigue pendiente mientras no se canceló ni se recibió del todo. */
const PENDING_STATUSES = new Set(["sent", "draft"]);

function formatDate(value: string | null): string {
  if (!value) return "sin fecha";
  return new Date(value).toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
}

function RecepcionesPage() {
  const [orders, setOrders] = useState<PurchaseOrderSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await pickingJson<{ purchaseOrders?: PurchaseOrderSummary[] }>(
        "/api/purchase-orders",
      );
      setOrders((data.purchaseOrders ?? []).filter((po) => PENDING_STATUSES.has(po.status)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudieron cargar las recepciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PickingShell
      title="Recepciones"
      backTo="/picking"
      actions={
        <Button
          size="icon"
          variant="ghost"
          className="h-9 w-9 shrink-0"
          onClick={() => void load()}
          aria-label="Actualizar"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      }
    >
      {() => (
        <div className="space-y-4">
          <Button asChild variant="outline" className="w-full justify-start">
            <Link to="/picking/recepciones/$poId" params={{ poId: "sin-oc" }}>
              <PackagePlus className="mr-2 h-4 w-4" />
              Recibir sin orden de compra
            </Link>
          </Button>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : orders.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No hay órdenes de compra pendientes de recibir.
            </p>
          ) : (
            <div className="space-y-2">
              {orders.map((po) => {
                const pending = po.items.filter((i) => i.receivedQuantity < i.quantity).length;
                return (
                  <Link
                    key={po.id}
                    to="/picking/recepciones/$poId"
                    params={{ poId: po.id }}
                    className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 active:bg-accent"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-foreground">
                          {po.supplierName ?? "Proveedor"}
                        </span>
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          {po.status === "sent" ? "Enviada" : "Borrador"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Esperada {formatDate(po.expectedAt)} · {pending} ítems por recibir
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </PickingShell>
  );
}
